// Dependency-free inline-SVG chart renderer for authored question content
// (bar / line / pie). Pure string-building — no canvas, no external chart
// library — matches the CSP-safe philosophy of lib/markdown.ts. Colors are the
// validated categorical order from the dataviz palette (fixed hue order, never
// cycled): blue, orange, aqua, yellow, magenta, green, violet, red.

export type ChartType = 'bar' | 'line' | 'pie';

export interface ChartData {
  type: ChartType;
  title?: string;
  labels: string[];
  values: number[];
}

const CATEGORICAL: { light: string; dark: string }[] = [
  { light: '#2a78d6', dark: '#3987e5' }, // blue
  { light: '#eb6834', dark: '#d95926' }, // orange
  { light: '#1baf7a', dark: '#199e70' }, // aqua
  { light: '#eda100', dark: '#c98500' }, // yellow
  { light: '#e87ba4', dark: '#d55181' }, // magenta
  { light: '#008300', dark: '#008300' }, // green
  { light: '#4a3aa7', dark: '#9085e9' }, // violet
  { light: '#e34948', dark: '#e66767' }, // red
];
const SERIES_1 = CATEGORICAL[0];

function esc(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function parseChartData(raw: string): ChartData | null {
  let json: unknown;
  try { json = JSON.parse(raw); } catch { return null; }
  if (!json || typeof json !== 'object') return null;
  const d = json as Record<string, unknown>;
  const type = d.type;
  if (type !== 'bar' && type !== 'line' && type !== 'pie') return null;
  const labels = d.labels;
  const values = d.values;
  if (!Array.isArray(labels) || !Array.isArray(values)) return null;
  if (labels.length === 0 || labels.length !== values.length) return null;
  if (!values.every((v) => typeof v === 'number' && Number.isFinite(v))) return null;
  if (!labels.every((l) => typeof l === 'string')) return null;
  return {
    type,
    title: typeof d.title === 'string' ? d.title : undefined,
    labels: labels as string[],
    values: values as number[],
  };
}

const W = 360;
const H = 220;
const PAD = { top: 24, right: 16, bottom: 34, left: 16 };

// A visually-hidden data table travels with every chart so the value is never
// gated behind an image — screen readers and "view data" both read the table.
function dataTable(data: ChartData): string {
  const rows = data.labels.map((l, i) => `<tr><td>${esc(l)}</td><td>${esc(String(data.values[i]))}</td></tr>`).join('');
  return `<table class="content-chart-table sr-only"><caption>${esc(data.title || 'Chart data')}</caption><thead><tr><th>Label</th><th>Value</th></tr></thead><tbody>${rows}</tbody></table>`;
}

function barChart(data: ChartData): string {
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const max = Math.max(1, ...data.values, 0);
  const n = data.values.length;
  const slot = innerW / n;
  const barW = Math.min(24, slot * 0.55);
  const gap = 2; // surface gap between adjacent bars
  const bars = data.values.map((v, i) => {
    const h = max > 0 ? (v / max) * innerH : 0;
    const x = PAD.left + i * slot + (slot - barW) / 2;
    const y = PAD.top + innerH - h;
    const label = data.labels[i];
    return `
      <rect x="${x + gap / 2}" y="${y}" width="${Math.max(0, barW - gap)}" height="${Math.max(0, h)}" rx="4" class="content-chart-bar" />
      <text x="${x + barW / 2}" y="${PAD.top + innerH + 14}" class="content-chart-axis" text-anchor="middle">${esc(label)}</text>
      <text x="${x + barW / 2}" y="${y - 4}" class="content-chart-value" text-anchor="middle">${esc(String(v))}</text>`;
  }).join('');
  return `<line x1="${PAD.left}" y1="${PAD.top + innerH}" x2="${W - PAD.right}" y2="${PAD.top + innerH}" class="content-chart-baseline" />${bars}`;
}

function lineChart(data: ChartData): string {
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const max = Math.max(1, ...data.values, 0);
  const min = Math.min(0, ...data.values);
  const range = max - min || 1;
  const n = data.values.length;
  const step = n > 1 ? innerW / (n - 1) : 0;
  const pts = data.values.map((v, i) => {
    const x = PAD.left + i * step;
    const y = PAD.top + innerH - ((v - min) / range) * innerH;
    return { x, y, v, label: data.labels[i] };
  });
  const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const dots = pts.map((p) => `
    <circle cx="${p.x}" cy="${p.y}" r="4" class="content-chart-dot" />
    <text x="${p.x}" y="${PAD.top + innerH + 14}" class="content-chart-axis" text-anchor="middle">${esc(p.label)}</text>`).join('');
  return `<line x1="${PAD.left}" y1="${PAD.top + innerH}" x2="${W - PAD.right}" y2="${PAD.top + innerH}" class="content-chart-baseline" />
    <path d="${path}" class="content-chart-line" fill="none" />${dots}`;
}

function pieChart(data: ChartData): string {
  const cx = W / 2;
  const cy = H / 2 - 6;
  const r = 70;
  const total = data.values.reduce((a, b) => a + b, 0) || 1;
  let angle = -Math.PI / 2;
  const slices = data.values.map((v, i) => {
    const frac = v / total;
    const start = angle;
    const end = angle + frac * Math.PI * 2;
    angle = end;
    const x1 = cx + r * Math.cos(start);
    const y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy + r * Math.sin(end);
    const large = end - start > Math.PI ? 1 : 0;
    const color = CATEGORICAL[i % CATEGORICAL.length];
    return `<path d="M${cx},${cy} L${x1.toFixed(2)},${y1.toFixed(2)} A${r},${r} 0 ${large} 1 ${x2.toFixed(2)},${y2.toFixed(2)} Z" style="fill:var(--cc-slot-${i % 8})" class="content-chart-slice" />`;
  }).join('');
  const legend = data.labels.map((l, i) => `
    <span class="content-chart-legend-item"><i style="background:var(--cc-slot-${i % 8})"></i>${esc(l)}</span>`).join('');
  return `<g transform="translate(0,-6)">${slices}</g>` + `<!--LEGEND-->${legend}`;
}

/** Renders a full <figure> block: title, SVG (or fallback), legend (pie), and a screen-reader table. */
export function renderChartFigure(rawJson: string): string {
  const data = parseChartData(rawJson);
  if (!data) return `<pre class="content-chart-error">Invalid chart data</pre>`;

  let body: string;
  let legend = '';
  if (data.type === 'bar') body = barChart(data);
  else if (data.type === 'line') body = lineChart(data);
  else {
    const full = pieChart(data);
    const [svgPart, legendPart] = full.split('<!--LEGEND-->');
    body = svgPart;
    legend = `<div class="content-chart-legend">${legendPart}</div>`;
  }

  const ariaLabel = `${data.type} chart${data.title ? `: ${data.title}` : ''}. ${data.labels.map((l, i) => `${l} ${data.values[i]}`).join(', ')}`;

  return `<figure class="content-chart">
    ${data.title ? `<figcaption>${esc(data.title)}</figcaption>` : ''}
    <svg viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(ariaLabel)}">${body}</svg>
    ${legend}
    ${dataTable(data)}
  </figure>`;
}
